import { Injectable } from '@angular/core';
import { IBrandResources } from 'src/app/domain/interfaces/brand/interface-brand-resources';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { first, map } from 'rxjs/operators';
import { SourceOfResource } from 'src/app/domain/models/resources/source-of-resource-enum';
import { AlertService } from '../alert-service/alert.service';
import { HeaderStyleService } from '../../services-views/header-style-service/header-style.service';
import { LoadingType } from 'src/app/domain/models/alert/loading-type-enum';

@Injectable({
  providedIn: 'root'
})
export class BrandService {
  private brandResources: BehaviorSubject<IBrandResources> = new BehaviorSubject<IBrandResources>({});
  private allBrandResources: BehaviorSubject<IBrandResources[]> = new BehaviorSubject<IBrandResources[]>([]);

  constructor(private http: HttpClient, private alertService: AlertService, private headerStyleService: HeaderStyleService) { }

  public getBrandResources(): Observable<IBrandResources> {
    return this.brandResources?.asObservable().pipe();
  }

  public getAllBrandResource(): Observable<IBrandResources[]> {
    return this.allBrandResources?.asObservable().pipe();
  }

  public initBrandResources(sourceOfResource = SourceOfResource.S3) {
    this.alertService.setLoading(true, LoadingType.Default, ['Loading...']);
    this.requestBrandResources(sourceOfResource).pipe(first()).subscribe({
      next: (brandResourcesResponse: IBrandResources) => {
        if (!brandResourcesResponse?.domains?.length) {
          if (sourceOfResource == SourceOfResource.S3) {
            this.initBrandResources(SourceOfResource.AdminAPI);
            return;
          }
          else if (sourceOfResource == SourceOfResource.AdminAPI) {
            this.initBrandResources(SourceOfResource.JSONInCodeBase);
          }
        }
        this.alertService.setLoading(false);
        this.setHeaderLogo(brandResourcesResponse);
      },
      error: (error: any) => {
        if (sourceOfResource == SourceOfResource.S3) {
          this.initBrandResources(SourceOfResource.AdminAPI);
        } else {
          this.alertService.setLoading(false);
          this.initBrandResources(SourceOfResource.JSONInCodeBase);
        }
      },
    });
  }

  private requestBrandResources(sourceOfResource: SourceOfResource): Observable<IBrandResources> {
    switch (sourceOfResource) {
      case SourceOfResource.S3:
        return this.getBrandsResourcesFromS3().pipe(map((brandResourcesResponse: IBrandResources) => {
          this.brandResources?.next(brandResourcesResponse);
          return brandResourcesResponse;
        }));
      case SourceOfResource.JSONInCodeBase:
        return this.getBrandsResourcesFromJSON().pipe(map((brandResourcesResponse: Array<IBrandResources>) => {
          this.allBrandResources.next(brandResourcesResponse);
          return this.getBrandResourcesAccordingToBrand(brandResourcesResponse, sourceOfResource);
        }));
      default:
        return this.getBrandsResourcesFromAdminAPI().pipe(map((brandResourcesResponse: Array<IBrandResources>) => {
          this.allBrandResources.next(brandResourcesResponse);
          return this.getBrandResourcesAccordingToBrand(brandResourcesResponse, sourceOfResource);
        }));
    }
  }

  private getBrandResourcesAccordingToBrand(brandResourcesResponse: Array<IBrandResources>, sourceOfResource: SourceOfResource): IBrandResources {
    let brandResources: IBrandResources = brandResourcesResponse?.find((resources => resources?.domains?.includes(window.location.origin)))!;
    if (brandResources) brandResources.gotFrom = sourceOfResource;
    //console.log("brand resources (from " + SourceOfResource[sourceOfResource] + ") -> ", brandResources);
    this.brandResources?.next(brandResources);
    return brandResources;
  }

  private getBrandsResourcesFromAdminAPI(): Observable<Array<IBrandResources>> {
    return this.http.get<{ data: Array<IBrandResources> }>(`${environment.apiNodeUrl}/brand-resources`).pipe(map((brandsResourcesResponse: { data: Array<IBrandResources> }) => {
      return brandsResourcesResponse?.data;
    }));
  }

  private getBrandsResourcesFromS3(): Observable<IBrandResources> {
    return this.http.get<{ data: IBrandResources }>(`${environment.apiNodeUrl}/brand-resources-filter-in-s3?domain=${window.location.origin}`, {
    }).pipe(map((brandsResourcesResponse: { data: IBrandResources }) => {
      return brandsResourcesResponse.data;
    }));
  }

  private getBrandsResourcesFromJSON(): Observable<Array<IBrandResources>> {
    return this.http.get<any>('assets/data/default-brands-resources.json').pipe(
      map((response: any) => response)
    );
  }

  private setHeaderLogo(brandResources: IBrandResources) {
    this.headerStyleService.setHeaderStyles({
      opacity: 1,
      logo: environment.assetsURL + brandResources?.pathLogoWhite!,
      class: "bg-dark"
    });
  }
}